import * as React from "react";

// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import type { ThemeProviderProps } from "@fluentui/react";
// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import { ThemeProvider } from "@fluentui/react";
import {
  FluentProvider,
  useFluent,
  makeStyles,
  mergeClasses,
  useThemeClassName,
  createCSSRuleFromTheme,
} from "@fluentui/react-components";
import type {
  GriffelStyle,
  Theme,
  FluentProviderProps,
} from "@fluentui/react-components";
import { Provider_unstable as DirProvider } from "@fluentui/react-shared-contexts";
import { useFocusVisible } from "@fluentui/react-tabster";
import { TextDirectionProvider } from "@griffel/react";
import { HeadlessFluentProvider } from "@fluentui-contrib/react-headless-provider";

import { AppThemeManager, AppTheme } from "../AppThemeManager";

export interface AppThemeProviderProps {
  children: React.ReactNode;
  fluentProviderProps?: FluentProviderProps;
  themeProviderProps?: ThemeProviderProps;
  useExistingThemeClassName?: boolean;
  useHeadlessThemeProvider?: boolean;
}

// Forces an update so theme is passed down properly
function useForceUpdate() {
  const [, setValue] = React.useState(0);
  return () =>
    setValue((value) => {
      return value + 1;
    });
}

function useGlobalTheme(): Theme {
  const appThemeManager = AppThemeManager.getInstance();
  const forceUpdate = useForceUpdate();
  React.useEffect(() => {
    appThemeManager.registerOnThemeChanged(forceUpdate);
    return () => {
      appThemeManager.unregisterOnThemeChanged(forceUpdate);
    };
  });
  return appThemeManager.getThemeNext();
}

/**
 * Custom hook that returns the current theme and theme mode.
 * It's a similar hook to useGlobalTheme but since the return type is different,
 * it's separated to avoid breaking changes.
 */
function useGlobalThemeHeadless(): [Theme, AppTheme] {
  const appThemeManager = AppThemeManager.getInstance();
  const forceUpdate = useForceUpdate();
  React.useEffect(() => {
    appThemeManager.registerOnThemeChanged(forceUpdate);
    return () => {
      appThemeManager.unregisterOnThemeChanged(forceUpdate);
    };
  });
  return [appThemeManager.getThemeNext(), appThemeManager.getThemeMode()];
}

const baseRootStyles: GriffelStyle = {
  backgroundColor: "transparent",
  width: "inherit",
  height: "inherit",
  display: "inherit",
  flexGrow: "inherit",
  flexShrink: "inherit",
  flexBasis: "inherit",
  position: "static",
  maxHeight: "inherit",
};

const useFluentProviderDefaultRootStyles = makeStyles({
  root: {
    lineHeight: "unset",
    ...baseRootStyles,
  },
});

const useThemeProviderDefaultRootStyles = makeStyles({
  root: baseRootStyles,
});

let globalThemeClassName: string | undefined = undefined;

const GetThemeClassNameHOC: React.FunctionComponent<{
  children?: React.ReactNode;
}> = (props: { children?: React.ReactNode }) => {
  const { children } = props;
  globalThemeClassName = useThemeClassName();

  return <>{children}</>;
};

const ThemeClassNameProvider: React.FunctionComponent<{
  children?: React.ReactNode;
  className: string;
  dir: "ltr" | "rtl";
  targetDocument?: Document;
}> = (props) => {
  const { children, className, dir, targetDocument } = props;
  const dirProviderValue = React.useMemo(
    () => ({ dir, targetDocument }),
    [dir, targetDocument]
  );

  const ref = useFocusVisible<HTMLDivElement>();

  return (
    <DirProvider value={dirProviderValue}>
      <div className={className} ref={ref}>
        {children}
      </div>
    </DirProvider>
  );
};

const STYLE_EL_ID = "modern-fluent-theme";
let headlessThemeEnabled: boolean = false;
let currentThemeMode: AppTheme = AppTheme.Light;
export const setHeadlessThemeIsEnabled = (isEnabled: boolean) => {
  // Set the flight check boolean
  headlessThemeEnabled = isEnabled;
};

function useModernFluentThemeInjector(
  targetDocument: Document,
  theme: Theme,
  themeMode: AppTheme
) {
  const [isThemeInjectionSuccessful, setThemeInjectionSuccessful] =
    React.useState(true);
  const memoizedThemeCSS = useMemoizedThemeCSS(theme);

  React.useLayoutEffect(() => {
    if (!memoizedThemeCSS) {
      setThemeInjectionSuccessful(false);
      return;
    }
    const styleEl = targetDocument.getElementById(
      STYLE_EL_ID
    ) as HTMLStyleElement | null;
    if (styleEl) {
      if (currentThemeMode !== themeMode) {
        styleEl.textContent = memoizedThemeCSS;
        setThemeInjectionSuccessful(true);
        currentThemeMode = themeMode;
      }
      return;
    }
    try {
      // Create the style element and inject it into the head
      const newStyleEl = targetDocument.createElement("style");
      newStyleEl.id = STYLE_EL_ID;
      newStyleEl.textContent = memoizedThemeCSS;
      targetDocument.head.appendChild(newStyleEl);
      setThemeInjectionSuccessful(true);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error injecting theme", error);
      setThemeInjectionSuccessful(false);
    }
  }, [targetDocument, theme, themeMode, memoizedThemeCSS]);

  return isThemeInjectionSuccessful;
}

const useHeadlessWrapperStyles = makeStyles({
  root: {
    display: "contents",
    outline: "none",
    background: "none",
    border: "none",
    padding: 0,
    margin: 0,
  },
});

const HeadlessProviderWrapper: React.FunctionComponent<{
  children?: React.ReactNode;
  dir: "ltr" | "rtl";
  targetDocument?: Document;
}> = (props) => {
  const { children, dir, targetDocument } = props;
  const [theme, themeMode] = useGlobalThemeHeadless();
  const modernThemeInjectionSuccessful = useModernFluentThemeInjector(
    targetDocument || document,
    theme,
    themeMode
  );
  const ref = useFocusVisible<HTMLDivElement>();
  const headlessWrapperStyles = useHeadlessWrapperStyles();

  if (modernThemeInjectionSuccessful) {
    return (
      <HeadlessFluentProvider dir={dir} targetDocument={targetDocument}>
        {/*
          The wrapper div uses display: contents to avoid affecting layout.
          The ref from useFocusVisible is required for focus visibility and keyboard navigation.
        */}
        <div ref={ref} dir={dir} className={headlessWrapperStyles.root}>
          {children}
        </div>
      </HeadlessFluentProvider>
    );
  }
  return null;
};

/**
 * Custom hook that memoizes the CSS rule generation for a given theme.
 * @param theme - The theme object used to generate CSS rules.
 * @returns The generated CSS rule as a string, or `null` if generation fails.
 */
const useMemoizedThemeCSS = (theme: Theme) => {
  return React.useMemo(() => {
    try {
      return createCSSRuleFromTheme("body", theme);
    } catch {
      return null;
    }
  }, [theme]);
};

// ADO 6369424: only using HOC as a workaround for bug in FluentProvider
const FluentProviderHOC = (props: {
  children?: React.ReactNode;
  fluentProviderProps?: FluentProviderProps;
  globalTheme: Theme;
  dir: "ltr" | "rtl";
  targetDocument?: Document;
  useExistingTheme?: boolean;
}) => {
  const {
    fluentProviderProps,
    globalTheme,
    dir,
    targetDocument,
    children,
    useExistingTheme,
  } = props;
  const fluentProviderStyles = useFluentProviderDefaultRootStyles();
  if (headlessThemeEnabled) {
    const headlessProviderWrapper = (
      <HeadlessProviderWrapper dir={dir} targetDocument={targetDocument}>
        {children}
      </HeadlessProviderWrapper>
    );
    if (headlessProviderWrapper) {
      return headlessProviderWrapper;
    }
  }
  return useExistingTheme && globalThemeClassName ? (
    <ThemeClassNameProvider
      className={globalThemeClassName}
      dir={dir}
      targetDocument={targetDocument}
    >
      {children}
    </ThemeClassNameProvider>
  ) : (
    <FluentProvider
      {...fluentProviderProps}
      className={mergeClasses(
        fluentProviderStyles.root,
        fluentProviderProps?.className
      )}
      theme={globalTheme}
      dir={dir}
    >
      <GetThemeClassNameHOC>{children}</GetThemeClassNameHOC>
    </FluentProvider>
  );
};

export const AppThemeProvider: React.FunctionComponent<
  AppThemeProviderProps
> = (props) => {
  const globalTheme = useGlobalTheme();
  const parentContext = useFluent();
  const themeProviderStyles = useThemeProviderDefaultRootStyles();

  const {
    children,
    fluentProviderProps,
    themeProviderProps,
    useExistingThemeClassName: useExistingTheme,
  } = props;

  const isRtl =
    // Check parentContext dir if it's set to RTL
    parentContext.dir === "rtl" ||
    // Fall back to checking the document body "dir" attribute
    // Adopted from Fluent v8's getRTL logic
    ((window.document.body && window.document.body.getAttribute("dir")) ||
      window.document.documentElement.getAttribute("dir")) === "rtl";
  const dir = isRtl ? "rtl" : "ltr";
  return (
    // ADO 6369424: only using TextDirectionProvider as a workaround for bug in FluentProvider
    <TextDirectionProvider dir={dir}>
      <FluentProviderHOC
        fluentProviderProps={fluentProviderProps}
        globalTheme={globalTheme}
        dir={dir}
        targetDocument={parentContext.targetDocument}
        useExistingTheme={useExistingTheme}
      >
        <ThemeProvider
          {...themeProviderProps}
          className={mergeClasses(
            themeProviderStyles.root,
            props.themeProviderProps?.className
          )}
        >
          {children}
        </ThemeProvider>
      </FluentProviderHOC>
    </TextDirectionProvider>
  );
};

AppThemeProvider.displayName = "AppThemeProvider";
